import { DateTime } from "luxon";

import { Client } from "@/Common/Api/Client";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { BaseSettingsContainer, BaseSettingsContainerProps, BaseSettingsContainerState } from "@/Common/Components/Settings/BaseSettingsContainer";
import { SearchSettingsFooterProps } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { ScreeningTestStatus } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { PreventiveActivitiesSearchSettings } from "@/Portfolio/Models/Settings/PreventiveActivitiesSearchSettings";

import "./PreventiveActivitiesSettingsContainer.css";

interface IPreventiveActivitiesSettingsContainer extends BaseSettingsContainerProps {
    onOkCallback: (model: PreventiveActivitiesSettingsContainerState) => void;
    savedState: PreventiveActivitiesSettingsContainerState;
}

class PreventiveActivitiesSettingsContainerState extends BaseSettingsContainerState {
    public globalClinicianList: EmployeeUIModel[];
    public globalRequestingStaffList: EmployeeUIModel[];
    public showAllClinicians: boolean;
    public showAllStatuses: boolean;
    public showAllSites: boolean;
    public showAllRequestingStaff: boolean;
    public clinicians: EmployeeUIModel[];
    public requestingStaff: EmployeeUIModel[];
    public dateFrom: Date;
    public dateTo: Date;
    public cliniciansSearchText: string;
    public requestingStaffSearchText: string;
    public sites: SiteUIModel[];
    public statuses: CheckboxUIModel[];
    public pageSize: number;
    public isOnlyWithinReminderPeriodChecked: boolean;

    public constructor(fields?: Partial<PreventiveActivitiesSettingsContainerState>) {
        super();
        Object.assign(this, fields);
    }

    public static getDefault(): PreventiveActivitiesSettingsContainerState {
        const state = new PreventiveActivitiesSettingsContainerState({
            showAllClinicians: false,
            showAllStatuses: false,
            showAllSites: false,
            showAllRequestingStaff: false,
            dateFrom: null,
            dateTo: null,
            cliniciansSearchText: "",
            requestingStaffSearchText: "",
            globalClinicianList: cacheStore
                .GetCliniciansAsEmployees(CheckboxTarget.Myle_Communications_Messages_Search_Clinician, true)
                .mutate(clinician => clinician.Checked = (clinician.Id === sessionStore.getCurrentEmployee().Id)),
            globalRequestingStaffList: cacheStore
                .GetCliniciansAsEmployees(CheckboxTarget.Myle_Communications_Messages_Search_Clinician, true)
                .mutate(clinician => clinician.Checked = (clinician.Id === sessionStore.getCurrentEmployee().Id)),
            pageSize: cacheStore.getDefaultPageSize(),
            isOnlyWithinReminderPeriodChecked: false,
            isSaveSettingsChecked: false,
            sites: (cacheStore.SiteCache ?? [])
                .map(site => {
                    const siteUi = SiteUIModel.FromCompositeObject(site);
                    siteUi.Checked = true;
                    return siteUi;
                }),
            statuses: (ScreeningTestStatus.GetAll() ?? [])
                .map(p => new CheckboxUIModel(
                    {
                        Id: p.LookupID,
                        Name: p.GetValue(),
                        Checked: (p.LookupCode === "SCREENINGTESTSTATUS_PENDING" || p.LookupCode === "SCREENINGTESTSTATUS_TODO")
                    }
                ))
        });

        state.clinicians = state.globalClinicianList;
        state.requestingStaff = state.globalRequestingStaffList;
        PreventiveActivitiesSettingsContainerState.setSearchSettingsFromUserPreferences(state);
        return state;
    }

    private static setSearchSettingsFromUserPreferences(state: PreventiveActivitiesSettingsContainerState): void {
        const settings = cacheStore.UserSettings?.PreventiveActivitiesSearchSettings;
        if (!settings)
            return;

        state.showAllClinicians = settings.ShowAllClinicians || state.showAllClinicians;
        state.showAllRequestingStaff = settings.ShowAllRequestingStaff || state.showAllRequestingStaff;
        state.showAllSites = settings.ShowAllSites || state.showAllSites;
        state.showAllStatuses = settings.ShowAllStatuses || state.showAllStatuses;
        state.isOnlyWithinReminderPeriodChecked = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff
            ? settings.IsOnlyWithinReminderPeriodChecked || state.isOnlyWithinReminderPeriodChecked
            : false;

        if (settings.ClinicianIds) {
            state.clinicians.mutate(c => c.Checked = settings.ClinicianIds.contains(c.Id));
            state.globalClinicianList.mutate(p => p.Checked = settings.ClinicianIds.contains(p.Id));
        }

        if (settings.RequestingStaffIds) {
            state.requestingStaff.mutate(c => c.Checked = settings.RequestingStaffIds.contains(c.Id));
            state.globalRequestingStaffList.mutate(p => p.Checked = settings.RequestingStaffIds.contains(p.Id));
        }

        if (settings.Statuses)
            state.statuses.mutate(c => c.Checked = settings.Statuses.contains(c.Id));

        if (settings.SiteIds)
            state.sites.mutate(s => s.Checked = settings.SiteIds.contains(s.Id));
    }
}

class PreventiveActivitiesSettingsContainer extends BaseSettingsContainer<IPreventiveActivitiesSettingsContainer, PreventiveActivitiesSettingsContainerState> {
    public constructor(props: IPreventiveActivitiesSettingsContainer) {
        super(props);
        this.state = PreventiveActivitiesSettingsContainerState.getDefault();
    }

    public componentDidMount(): void {
        if (this.props.savedState) {
            this.setState({
                ...this.props.savedState,
                statuses: this.props.savedState.statuses.mutate(p => p.Name = cacheStore.GetLookupById(p.Id).GetValue()),
                isSaveSettingsChecked: false
            });
        }
    }

    protected getHeaders(): { node: React.ReactNode; id: string; }[] {
        const requestingStaffNode = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff
            ? {
                id: "allRequestingStaff",
                node: <CheckboxControl
                    label={cacheStore.GetString("RequestingStaffLabel").toUpperCase()}
                    checked={this.state.showAllRequestingStaff}
                    onChange={checked => this.mutateState(
                        state => {
                            state.showAllRequestingStaff = checked;
                            state.globalRequestingStaffList.forEach(p => p.Checked = checked);
                            state.requestingStaff.forEach(p => p.Checked = checked);
                        }
                    )}
                />
            }
            : {
                id: "allClinicians",
                node: <CheckboxControl
                    label={cacheStore.GetString("ContributorsDLabel").toUpperCase()}
                    checked={this.state.showAllClinicians}
                    onChange={checked => this.mutateState(
                        state => {
                            state.showAllClinicians = checked;
                            state.globalClinicianList.forEach(p => p.Checked = checked);
                            state.clinicians.forEach(p => p.Checked = checked);
                        }
                    )}
                />
            };

        return [
            {
                id: "DueDates",
                node: cacheStore.GetString("DueDateLabel").toUpperCase()
            },
            {
                id: "allSites",
                node: this.state.sites.length > 1
                    ? (
                        <CheckboxControl
                            label={cacheStore.GetString("SitesLabel").toUpperCase()}
                            checked={this.state.showAllSites}
                            onChange={checked => {
                                this.mutateState(
                                    state => {
                                        state.showAllSites = checked;
                                        state.sites.forEach(p => p.Checked = checked);
                                    },
                                    () => this.updateEmployeeList()
                                );
                            }}
                        />
                    )
                    : null
            },
            requestingStaffNode,
            {
                id: "allStatus",
                node: <CheckboxControl
                    label={cacheStore.GetString("StatusLabel").toUpperCase()}
                    checked={this.state.showAllStatuses}
                    onChange={checked => this.mutateState(
                        state => {
                            state.showAllStatuses = checked;
                            state.statuses.forEach(p => p.Checked = checked);
                            state.isOnlyWithinReminderPeriodChecked = checked;
                        }
                    )}
                />
            }
        ]
            .filter(p => !!p.node);
    }

    protected getColumnData(): { node: React.ReactNode; id: string; }[] {
        const isPreventiveActivityRequestingStaffEnabled = cacheStore.ClinicSettings.EnablePreventiveActivityRequestingStaff;

        return [
            {
                id: "dueDateSelection",
                node: (
                    <div>
                        <MultiDateSelector
                            id="dueDate-time-span-search"
                            fromChange={date => this.mutateState(state => state.dateFrom = date && DateTime.fromJSDate(date).startOf("day").toJSDate())}
                            toChange={date => this.mutateState(state => state.dateTo = date && DateTime.fromJSDate(date).endOf("day").toJSDate())}
                            fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            from={this.state.dateFrom}
                            to={this.state.dateTo}
                        />
                    </div>
                )
            },
            {
                id: "sitesSelection",
                node: this.state.sites.length > 1 && (
                    <div className="sites-column">
                        <div className="prevent-settings sites-control">
                            <CheckedListControl
                                DataSource={this.state.sites.toImmutable()}
                                onChange={(checked, id) => this.mutateState(
                                    state => state.sites.firstOrDefault(p => p.Id === id).Checked = checked,
                                    () => this.updateEmployeeList()
                                )}
                                MaxTextLength={25}
                            />
                        </div>
                    </div>
                )
            },
            {
                id: isPreventiveActivityRequestingStaffEnabled ? "requestingStaffSelect" : "cliniciansSelect",
                node: (
                    <div>
                        <input
                            className="form-control prevent-settings search-text"
                            value={isPreventiveActivityRequestingStaffEnabled ? this.state.requestingStaffSearchText : this.state.cliniciansSearchText}
                            onChange={e => {
                                this.mutateState(state => {
                                    if (isPreventiveActivityRequestingStaffEnabled) {
                                        state.requestingStaffSearchText = e.target.value;
                                    }
                                    else {
                                        state.cliniciansSearchText = e.target.value;
                                    }
                                }, () => {
                                    if (isPreventiveActivityRequestingStaffEnabled) {
                                        this.updateRequestingStaffList();
                                    }
                                    else {
                                        this.updateEmployeeList();
                                    }
                                });
                            }}
                            placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                        />
                        <div className="prevent-settings created-list-control">
                            <CheckedListControl
                                idModifier={isPreventiveActivityRequestingStaffEnabled ? "requestingStaffListList" : "cliniciansListList"}
                                DataSource={isPreventiveActivityRequestingStaffEnabled ? this.state.requestingStaff.toImmutable() : this.state.clinicians.toImmutable()}
                                onChange={(checked, id) =>
                                    this.mutateState(state => {
                                        if (isPreventiveActivityRequestingStaffEnabled) {
                                            state.requestingStaff.firstOrDefault(p => p.Id === id).Checked = checked;
                                            state.globalRequestingStaffList.firstOrDefault(p => p.Id === id).Checked = checked;
                                        }
                                        else {
                                            state.clinicians.firstOrDefault(p => p.Id === id).Checked = checked;
                                            state.globalClinicianList.firstOrDefault(p => p.Id === id).Checked = checked;
                                        }
                                    })
                                }
                                sort
                                limitTextWidth
                                enableLazyRendering
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "statusesSelect",
                node: (
                    <div className="prevent-settings status-controls">
                        <CheckedListControl
                            DataSource={this.state.statuses.toImmutable()}
                            onChange={(checked, id) => {
                                this.mutateState(state => state.statuses.firstOrDefault(p => p.Id === id).Checked = checked);
                                if (!checked && id === ScreeningTestStatus.Todo.LookupID) {
                                    this.mutateState(state => state.isOnlyWithinReminderPeriodChecked = false);
                                }
                            }}
                        />
                        {isPreventiveActivityRequestingStaffEnabled && (
                            <CheckboxControl
                                label={cacheStore.GetString("OnlyWithinReminderPeriodLabel")}
                                checked={this.state.isOnlyWithinReminderPeriodChecked}
                                onChange={checked => this.mutateState(state => {
                                    state.isOnlyWithinReminderPeriodChecked = checked;
                                    if (checked) {
                                        state.statuses.firstOrDefault(p => p.Id === ScreeningTestStatus.Todo.LookupID).Checked = true;
                                    }
                                })}
                                labelClassName="marginleft10"
                            />
                        )}
                    </div>
                )
            }
        ]
            .filter(p => !!p.node);
    }

    protected getSearchSettingsFooterProps(): SearchSettingsFooterProps {
        return {
            onClose: () => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_SettingsContainerVisibility),
            onOk: () => this.onOkSettings(),
            onColumnOrderingButtonClick: () => sessionStore.dispatch(LocalOperations.Portfolio.PreventiveActivitiesInbox_ColumnOrderingPopupVisibility, { isOpen: true }),
            onResultsPerPageChange: value => this.mutateState(state => state.pageSize = value),
            selectedPageSizeOption: this.state.pageSize,
            isSaveSettingsChecked: this.state.isSaveSettingsChecked,
            onSaveSettingsCheckboxChange: checked => this.mutateState(state => state.isSaveSettingsChecked = checked)
        };
    }

    protected shouldTriggerOkActionOnOutsideClick(): boolean {
        return true;
    }

    private updateEmployeeList(): void {
        this.mutateState(
            state => {
                const siteIds = this.state.sites.filter(site => site.Checked).map(p => p.Id);
                const filteredClinicians = state.globalClinicianList.filter(employee => employee.Sites.some(employeeSiteId => siteIds.contains(employeeSiteId)));

                state.clinicians = this.state.cliniciansSearchText?.trim()
                    ? SearchMenuHelper.FilterCliniciansByName(this.state.cliniciansSearchText?.trim(), filteredClinicians.toImmutable()).toArray()
                    : filteredClinicians;
            }
        );
    }

    private filterRequestingStaff(): EmployeeUIModel[] {
        const siteIds = this.state.sites.filter(site => site.Checked).map(p => p.Id);
        return this.state.globalRequestingStaffList.filter(employee => employee.Sites.some(employeeSiteId => siteIds.contains(employeeSiteId)));
    }

    private updateRequestingStaffList(): void {
        this.mutateState(
            state => {
                state.requestingStaff = !IsNullOrWhiteSpace(this.state.requestingStaffSearchText)
                    ? SearchMenuHelper.FilterCliniciansByName(this.state.requestingStaffSearchText?.trim(), state.globalRequestingStaffList.toImmutable()).toArray()
                    : this.filterRequestingStaff();
            }
        );
    }

    private onOkSettings(): void {
        if (this.state.isSaveSettingsChecked)
            this.saveUserSetting();

        this.props.onOkCallback(this.state);
    }

    private async saveUserSetting(): Promise<void> {
        const userSettings = new UserSettings({
            PreventiveActivitiesSearchSettings: PreventiveActivitiesSearchSettings.fromModel(this.state)
        });

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        if (response.isSuccess)
            cacheStore.UserSettings.PreventiveActivitiesSearchSettings = userSettings.PreventiveActivitiesSearchSettings;
    }
}

export { PreventiveActivitiesSettingsContainer, PreventiveActivitiesSettingsContainerState };
